import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2, Download, Linkedin, Twitter, Award, Target, TrendingUp, Zap, Copy, Check, Loader2 } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const CareerReadinessScore = ({ roadmaps = [], analytics = null, user = null }) => {
    const [copied, setCopied] = useState(false);
    const [showCard, setShowCard] = useState(false);
    const [apiScore, setApiScore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Fetch from backend
    useEffect(() => {
        const fetchScore = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await featuresAPI.getReadinessScore();
                if (result?.overall_score != null) {
                    setApiScore(result);
                } else {
                    setError(true);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchScore();
    }, []);

    const scoreData = useMemo(() => {
        if (!apiScore) return null;

        return {
            overall: apiScore.overall_score || 0,
            skills: apiScore.sub_scores?.skills || 0,
            progress: apiScore.sub_scores?.progress || 0,
            consistency: apiScore.sub_scores?.consistency || 0,
            market: apiScore.sub_scores?.market_readiness || 0,
            careerPath: apiScore.career_path || (roadmaps.find(r => r.isActive) || roadmaps[0])?.careerPath || 'Career Path',
            tasksCompleted: apiScore.completed_tasks || 0,
            totalTasks: apiScore.total_tasks || 1,
            totalRoadmaps: roadmaps.length,
        };
    }, [apiScore, roadmaps]);

    const getScoreColor = (score) => {
        if (score >= 80) return { color: '#10b981', label: 'Excellent', bg: 'from-emerald-500/20 to-teal-500/10' };
        if (score >= 60) return { color: '#f59e0b', label: 'Good', bg: 'from-amber-500/20 to-yellow-500/10' };
        if (score >= 40) return { color: '#3b82f6', label: 'On Track', bg: 'from-blue-500/20 to-cyan-500/10' };
        return { color: '#ef4444', label: 'Getting Started', bg: 'from-red-500/20 to-pink-500/10' };
    };

    const shareText = scoreData
        ? `🚀 My Career Readiness Score: ${scoreData.overall}/100 for ${scoreData.careerPath}! I've completed ${scoreData.tasksCompleted} tasks on my learning journey. Check your score at Rastor AI! #Rastor AI #CareerGrowth`
        : '';

    const handleShare = (platform) => {
        const url = 'https://rastorai.com';
        if (platform === 'linkedin') {
            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(shareText)}`, '_blank');
        } else if (platform === 'twitter') {
            window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`, '_blank');
        } else {
            navigator.clipboard.writeText(shareText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Award className="w-5 h-5 text-amber-400" />
                    <h3 className="font-bold text-white text-sm">Career Readiness Score</h3>
                </div>
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                    <span className="ml-2 text-xs text-slate-500">Calculating your score...</span>
                </div>
            </div>
        );
    }

    // Error / no data state
    if (error || !scoreData) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Award className="w-5 h-5 text-amber-400" />
                    <h3 className="font-bold text-white text-sm">Career Readiness Score</h3>
                </div>
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Award className="w-10 h-10 text-slate-700 mb-3" />
                    <p className="text-sm text-slate-400 mb-1">Score not available yet</p>
                    <p className="text-xs text-slate-600">Start a roadmap and complete tasks to see your readiness score</p>
                </div>
            </div>
        );
    }

    const { color, label, bg } = getScoreColor(scoreData.overall);
    const circumference = 2 * Math.PI * 54;
    const offset = circumference - (scoreData.overall / 100) * circumference;

    const subScores = [
        { label: 'Skills', value: scoreData.skills, icon: <Target className="w-3 h-3" /> },
        { label: 'Progress', value: scoreData.progress, icon: <TrendingUp className="w-3 h-3" /> },
        { label: 'Consistency', value: scoreData.consistency, icon: <Zap className="w-3 h-3" /> },
        { label: 'Market Fit', value: scoreData.market, icon: <Award className="w-3 h-3" /> },
    ];

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl overflow-hidden">
            <div className={`bg-gradient-to-r ${bg} p-6 border-b border-white/5`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Award className="w-5 h-5" style={{ color }} />
                        <h3 className="font-bold text-white text-sm">Career Readiness Score</h3>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                            style={{ color, background: `${color}20` }}>
                            {label}
                        </span>
                    </div>
                    <button
                        onClick={() => setShowCard(!showCard)}
                        className="text-slate-500 hover:text-white transition-colors"
                    >
                        <Share2 className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex items-center gap-6">
                    {/* Circular Progress */}
                    <div className="relative">
                        <svg width="128" height="128" viewBox="0 0 128 128">
                            <circle cx="64" cy="64" r="54" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                            <motion.circle
                                cx="64" cy="64" r="54"
                                fill="none"
                                stroke={color}
                                strokeWidth="8"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                initial={{ strokeDashoffset: circumference }}
                                animate={{ strokeDashoffset: offset }}
                                transition={{ duration: 1.5, ease: 'easeOut' }}
                                transform="rotate(-90 64 64)"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.span
                                className="text-3xl font-bold text-white"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                {scoreData.overall}
                            </motion.span>
                            <span className="text-[10px] text-slate-500 font-mono">/ 100</span>
                        </div>
                    </div>

                    {/* Sub-scores */}
                    <div className="flex-1 grid grid-cols-2 gap-2">
                        {subScores.map((sub, i) => (
                            <motion.div
                                key={sub.label}
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 + i * 0.1 }}
                                className="bg-[#0B0F19]/50 rounded-lg p-2.5"
                            >
                                <div className="flex items-center gap-1 mb-1">
                                    <span className="text-slate-500">{sub.icon}</span>
                                    <span className="text-[10px] text-slate-500 font-mono uppercase">{sub.label}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            style={{ background: color }}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${sub.value}%` }}
                                            transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
                                        />
                                    </div>
                                    <span className="text-xs font-bold text-slate-400">{sub.value}</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Share Card */}
            {showCard && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="p-4 border-t border-white/5"
                >
                    <div className="flex items-center gap-2">
                        <button onClick={() => handleShare('linkedin')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#0A66C2]/10 border border-[#0A66C2]/20 rounded-lg text-[#0A66C2] text-xs hover:bg-[#0A66C2]/20 transition-colors">
                            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                        </button>
                        <button onClick={() => handleShare('twitter')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#1DA1F2]/10 border border-[#1DA1F2]/20 rounded-lg text-[#1DA1F2] text-xs hover:bg-[#1DA1F2]/20 transition-colors">
                            <Twitter className="w-3.5 h-3.5" /> Twitter
                        </button>
                        <button onClick={() => handleShare('copy')}
                            className="flex items-center justify-center px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 text-xs hover:bg-white/10 transition-colors">
                            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default CareerReadinessScore;
